import { FunctionComponent, memo, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EventDetailsModalContext, EventDetailsTab } from '../../../../../context';
import { Event } from '../../../../../services';
import { Avatar } from '../../../Avatar';

import classNames from 'classnames';
import classes from './EventParticipants.module.scss';

interface EventParticipantsProps {
  event: Event;
  onSelectTab?: (tab: EventDetailsTab) => void;
}

export const EventParticipants: FunctionComponent<EventParticipantsProps> = memo(
  ({ event, onSelectTab }) => {
    const { t: ctxT } = useTranslation('', { keyPrefix: 'eventItem' });

    const { setEventDetailsModalData } = useContext(EventDetailsModalContext);

    const {
      id: eventId,
      showParticipants,
      participantsPreview,
      participantCountConfirmed,
      participantCountWaitlisted,
      participantsCapacity,
      allowWaitlist,
      permissions: { allowToSeeParticipants, allowToSeeWaitlisted },
    } = event;

    const isFull = useMemo(
      () => participantsCapacity > 0 && participantCountConfirmed >= participantsCapacity,
      [participantsCapacity, participantCountConfirmed]
    );

    const participantsText = useMemo(() => {
      if (isFull && allowWaitlist && participantsCapacity > 0) {
        return `${participantCountConfirmed}/${participantsCapacity}`;
      }
      if (participantsCapacity > 0) {
        return ctxT('attending-with-capacity', {
          count: participantCountConfirmed,
          capacity: participantsCapacity,
        });
      }
      return ctxT('attending-count', { count: participantCountConfirmed });
    }, [ctxT, isFull, allowWaitlist, participantCountConfirmed, participantsCapacity]);

    const openDetailsTab = useCallback(
      (initialTab: EventDetailsTab) => {
        if (onSelectTab) {
          onSelectTab(initialTab);
          return;
        }
        setEventDetailsModalData({ eventId, initialTab });
      },
      [onSelectTab, setEventDetailsModalData, eventId]
    );

    if (!showParticipants || !participantCountConfirmed) {
      return null;
    }

    const ConfirmedTag = allowToSeeParticipants ? 'button' : 'div';

    const WaitlistTag = allowToSeeWaitlisted ? 'button' : 'span';

    return (
      <div className={classes['event-participants']}>
        <ConfirmedTag
          className={classes['event-participants__confirmed']}
          onClick={() => allowToSeeParticipants && openDetailsTab(EventDetailsTab.PARTICIPANTS)}
          {...(allowToSeeParticipants && { type: 'button' })}
        >
          {participantsPreview.map(({ screenName, avatar }) => (
            <Avatar
              key={screenName}
              className={classes['event-participants__avatar']}
              url={avatar?.url}
              size={28}
              nonClickable
            />
          ))}
          <span
            className={classNames(classes['event-participants__count'], {
              [classes['event-participants__count--full']]: isFull,
            })}
          >
            {participantsText}
          </span>
        </ConfirmedTag>
        {isFull && allowWaitlist && (
          <WaitlistTag
            className={classes['event-participants__waitlist']}
            onClick={() => allowToSeeWaitlisted && openDetailsTab(EventDetailsTab.WAITLIST)}
            {...(allowToSeeWaitlisted && { type: 'button' })}
          >
            {ctxT('on-waitlist', { count: participantCountWaitlisted })}
          </WaitlistTag>
        )}
      </div>
    );
  }
);
